import { MonitoringGroupedPartInput, MonitoringGroupedPartSourceItem } from "./monitoring-grouped-parts.types";

export interface PatientMonitorGroupedPartLabels {
  family: string;
  sensor: string;
  mountingGroup: string;
  mountingArm: string;
  attachment: string;
}

export interface CentralStationGroupedPartLabels {
  base: string;
  licence: string;
  additional: string;
  accessory: string;
}

export interface TelemetryGroupedPartLabels {
  family: string;
  sensor: string;
  additionalBattery: string;
}

export interface KvmGroupedPartLabels {
  family: string;
  transmitter: string;
  receiver: string;
  accessories: string;
}

export interface DisplayGroupedPartLabels {
  display: string;
  accessory: string;
}

export interface UtilityGroupedPartLabels {
  device: string;
}

export interface PatientMonitorGroupedPartInputs {
  family?: MonitoringGroupedPartInput | null;
  sensor?: MonitoringGroupedPartInput | null;
  mountingGroup?: MonitoringGroupedPartInput | null;
  mountingArm?: MonitoringGroupedPartInput | null;
  attachments?: MonitoringGroupedPartInput[] | null;
}

export interface CentralStationGroupedPartInputs {
  baseOptions?: MonitoringGroupedPartInput[] | null;
  licence?: MonitoringGroupedPartInput | null;
  additionalLicences?: MonitoringGroupedPartInput[] | null;
  accessories?: MonitoringGroupedPartInput[] | null;
}

export interface TelemetryGroupedPartInputs {
  family?: MonitoringGroupedPartInput | MonitoringGroupedPartInput[] | null;
  sensor?: MonitoringGroupedPartInput | null;
  additionalBatteries?: MonitoringGroupedPartInput[] | null;
}

export interface KvmGroupedPartInputs {
  family?: MonitoringGroupedPartInput | null;
  transmitter?: MonitoringGroupedPartInput | null;
  receiver?: MonitoringGroupedPartInput | null;
  accessories?: MonitoringGroupedPartInput[] | null;
}

export interface DisplayGroupedPartInputs {
  display?: MonitoringGroupedPartInput | null;
  accessories?: MonitoringGroupedPartInput[] | null;
}

export interface UtilityGroupedPartInputs {
  devices?: MonitoringGroupedPartInput[] | null;
}

export function createMonitoringGroupedSourceItem(
  input: MonitoringGroupedPartInput,
  category: string,
): MonitoringGroupedPartSourceItem {
  return {
    option: input.option,
    quantity: input.quantity,
    category,
  };
}

export function buildPatientMonitorGroupedSourceItems(
  inputs: PatientMonitorGroupedPartInputs,
  labels: PatientMonitorGroupedPartLabels,
): MonitoringGroupedPartSourceItem[] {
  return [
    ...toGroupedItems(inputs.family, labels.family),
    ...toGroupedItems(inputs.sensor, labels.sensor),
    ...toGroupedItems(inputs.mountingGroup, labels.mountingGroup),
    ...toGroupedItems(inputs.mountingArm, labels.mountingArm),
    ...toGroupedItems(inputs.attachments, labels.attachment),
  ];
}

export function buildCentralStationGroupedSourceItems(
  inputs: CentralStationGroupedPartInputs,
  labels: CentralStationGroupedPartLabels,
): MonitoringGroupedPartSourceItem[] {
  return [
    ...toGroupedItems(inputs.baseOptions, labels.base),
    ...toGroupedItems(inputs.licence, labels.licence),
    ...toGroupedItems(inputs.additionalLicences, labels.additional),
    ...toGroupedItems(inputs.accessories, labels.accessory),
  ];
}

export function buildTelemetryGroupedSourceItems(
  inputs: TelemetryGroupedPartInputs,
  labels: TelemetryGroupedPartLabels,
): MonitoringGroupedPartSourceItem[] {
  return [
    ...toGroupedItems(inputs.family, labels.family),
    ...toGroupedItems(inputs.sensor, labels.sensor),
    ...toGroupedItems(inputs.additionalBatteries, labels.additionalBattery),
  ];
}

export function buildKvmGroupedSourceItems(
  inputs: KvmGroupedPartInputs,
  labels: KvmGroupedPartLabels,
): MonitoringGroupedPartSourceItem[] {
  return [
    ...toGroupedItems(inputs.family, labels.family),
    ...toGroupedItems(inputs.transmitter, labels.transmitter),
    ...toGroupedItems(inputs.receiver, labels.receiver),
    ...toGroupedItems(inputs.accessories, labels.accessories),
  ];
}

export function buildDisplayGroupedSourceItems(
  inputs: DisplayGroupedPartInputs,
  labels: DisplayGroupedPartLabels,
): MonitoringGroupedPartSourceItem[] {
  return [...toGroupedItems(inputs.display, labels.display), ...toGroupedItems(inputs.accessories, labels.accessory)];
}

export function buildUtilityGroupedSourceItems(
  inputs: UtilityGroupedPartInputs,
  labels: UtilityGroupedPartLabels,
): MonitoringGroupedPartSourceItem[] {
  return [...toGroupedItems(inputs.devices, labels.device)];
}

function toGroupedItems(
  input: MonitoringGroupedPartInput | MonitoringGroupedPartInput[] | null | undefined,
  category: string,
): MonitoringGroupedPartSourceItem[] {
  if (!input) return [];

  const inputs = Array.isArray(input) ? input : [input];

  return inputs.map((item) => createMonitoringGroupedSourceItem(item, category));
}
